import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CircleCheckboxInput from 'components/Luxkit/Checkbox/CircleCheckboxInput'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import useToggle from 'hooks/useToggle'

type Props = {
  heading: string
  subHeading?: string
  showBorder?: boolean
  image?: App.Image
  imgUrl?: string
  onSelect?: (checked: boolean) => void
}

const Root = styled(Clickable)`
  display: flex;
  gap: 12px;
  border-radius: ${(props) => props.theme.borderRadius.S};
  align-items: center;
  padding: ${rem(20)};
  width: 100%;

  &.hide-border {
    padding: 0;
  }

  &.package-container {
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    gap: 12px;

    .package-items {
      flex-direction: column;
    }
  }

  > .click-disabled {
    pointer-events: none;
  }
`

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
`

const ImgContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  display: flex;
  justify-content: center;
  flex-direction: column;
  flex-shrink: 0;
`

const BookingItemImage = styled(ResponsiveImage)`
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const ImagePlaceHolder = styled.div`
  min-height: 48px;
  min-width: 48px;
  background: ${({ theme }) => theme.palette.neutral.default.six};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

function BookingItemCard(props: Props) {
  const {
    heading,
    subHeading,
    image,
    showBorder = true,
    imgUrl,
    onSelect,
  } = props
  const { value, toggle } = useToggle(false)
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const onClick = useCallback(() => {
    toggle()
    onSelect?.(!value)
  }, [onSelect, toggle, value])

  return (
    // eslint-disable-next-line @typescript-eslint/naming-convention
    <Root className={cn({ 'hide-border': !showBorder })} onClick={onClick}>
      {showBorder && (
        <CircleCheckboxInput
          className="click-disabled"
          ref={checkBoxRef}
          checked={value}
        />
      )}
      {image?.id && (
        <BookingItemImage
          aria-hidden="true"
          alt="Package preview"
          id={image.id}
          mobileAspectRatio="1:1"
          width={48}
        />
      )}
      {!image && imgUrl && (
        <ImgContainer>
          <Img alt={heading} src={imgUrl} />
        </ImgContainer>
      )}
      {!image && !imgUrl && <ImagePlaceHolder />}
      <div>
        {!showBorder && (
          <BodyText lineClamp={1} variant="large" colour="neutral-one">
            {heading}
          </BodyText>
        )}
        {showBorder && (
          <Heading lineClamp={1} variant="heading6">
            {heading}
          </Heading>
        )}
        {subHeading && (
          <BodyText variant="large" colour="neutral-two">
            {subHeading}
          </BodyText>
        )}
      </div>
    </Root>
  )
}

type PackageBookingItemProps = Omit<Props, 'image'> & {
  children: React.ReactNode
}

export function PackageBookingItem(props: PackageBookingItemProps) {
  const { heading, subHeading, children, onSelect } = props
  const { value, toggle } = useToggle(false)
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const onClick = useCallback(() => {
    toggle()
    onSelect?.(!value)
  }, [onSelect, toggle, value])

  return (
    <Root className="package-container" onClick={onClick}>
      <CircleCheckboxInput
        className="click-disabled"
        ref={checkBoxRef}
        checked={value}
      />
      <VerticalSpacer gap={24} className="package-items">
        <div className="packageHeading">
          <Heading lineClamp={1} variant="heading6">
            {heading}
          </Heading>
          {subHeading && <BodyText variant="large">{subHeading}</BodyText>}
        </div>
        {children}
      </VerticalSpacer>
    </Root>
  )
}
export default BookingItemCard
